<template>
  <div id="wrapper">
    <SidebarView :budgets="true" :budgetsLines="true" />
    <div class="d-flex flex-column" id="content-wrapper">
      <div id="content">
        <TopbarView />
        <div class="container">
          <div class="card card-shape home-box">
            <div class="card-header">
              <div class="row align-items-center">
                <div class="col-8">
                  <h6 class="m-0 fw-bold text-green">Budget Lines</h6>
                  <div class="small text-muted">
                    Budget ID: {{ budgetId }}
                    <span v-if="budget?.status" class="ms-2 badge bg-secondary text-uppercase">
                      {{ budget.status }}
                    </span>
                  </div>
                </div>
                <div class="col-4 text-end">
                  <input
                    ref="excelInput"
                    type="file"
                    class="d-none"
                    :accept="acceptedFileTypes"
                    @change="onFileSelected"
                  />
                  <b-button
                    class="btn akkurate-green-btn text-white btn-size me-2"
                    :disabled="!canImportExcel || isUploading"
                    v-if="canManage"
                    @click="triggerFilePicker"
                  >
                    {{ isUploading ? "Importing..." : "Import Excel" }}
                  </b-button>
                  <b-button class="btn akkurate-gray-btn text-white btn-size" @click="downloadTemplate">
                    Download Template
                  </b-button>
                </div>
              </div>
            </div>
            <div class="card-body">
              <div v-if="canManage">
                <b-alert v-if="budget && !isDraft" show variant="warning">
                  Budget is <strong>{{ budget.status }}</strong>. This budget is read-only.
                </b-alert>

                <b-alert v-if="uploadError.message" show variant="danger">
                  <div class="fw-bold">{{ uploadError.message }}</div>
                  <div v-if="uploadError.errors.length" class="small mt-2">
                    Row-level errors are listed below.
                  </div>
                </b-alert>

              <div v-if="canManage && isDraft" class="row g-3 mb-3">
                <div class="col-md-3">
                  <label class="form-label small fw-bold">Month (optional)</label>
                  <input v-model="filters.month" type="month" class="form-control form-control-sm" />
                </div>
                <div class="col-md-3">
                  <label class="form-label small fw-bold">Branch (optional)</label>
                  <select v-model="filters.branch_id" class="form-select form-select-sm">
                    <option value="">All</option>
                    <option v-for="br in branchOptions" :key="br.id" :value="String(br.id)">
                      {{ br.name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-bold">COA Subhead (optional)</label>
                  <select v-model="filters.coa_subhead_id" class="form-select form-select-sm">
                    <option value="">All</option>
                    <option v-for="s in coaSubheadOptions" :key="s.id" :value="String(s.id)">
                      {{ s.code }} - {{ s.name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2 d-flex align-items-end">
                  <b-button class="btn akkurate-green-btn text-white btn-size" @click="loadLines">
                    Apply
                  </b-button>
                </div>
              </div>

              <div v-if="canManage && isDraft" class="card mb-3 border-0 shadow-sm">
                <div class="card-body p-3">
                  <div class="row g-3">
                    <div class="col-md-4">
                      <label class="form-label small fw-bold">COA Subhead</label>
                      <select v-model="upsert.coa_subhead_id" class="form-select form-select-sm">
                        <option value="">Select</option>
                        <option v-for="s in coaSubheadOptions" :key="s.id" :value="String(s.id)">
                          {{ s.code }} - {{ s.name }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label small fw-bold">Branch</label>
                      <select v-model="upsert.branch_id" class="form-select form-select-sm">
                        <option value="">Select</option>
                        <option v-for="br in branchOptions" :key="br.id" :value="String(br.id)">
                          {{ br.name }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-2">
                      <label class="form-label small fw-bold">Month</label>
                      <input v-model="upsert.month" type="month" class="form-control form-control-sm" />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label small fw-bold">Budget Amount (GH¢)</label>
                      <input v-model="upsert.budget_amount" type="number" step="0.01" class="form-control form-control-sm" />
                    </div>
                    <div class="col-12">
                      <b-button
                        class="btn akkurate-green-btn text-white btn-size"
                        :disabled="isUpserting || !canUpsert"
                        @click="upsertLine"
                      >
                        {{ isUpserting ? "Saving..." : "Upsert line" }}
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="uploadError.errors.length" class="mb-3">
                <b-table
                  :items="uploadError.errors"
                  :fields="uploadErrorFields"
                  small
                  responsive
                  striped
                />
              </div>

              <b-table
                v-if="!noAccess"
                :items="lines"
                :fields="lineFields"
                small
                responsive
                striped
                :busy.sync="isBusy"
              >
                <template #table-busy>
                  <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Loading...</strong>
                  </div>
                </template>
                <template #cell(actions)="{ item }">
                  <b-button
                    v-if="canManage && isDraft"
                    class="btn akkurate-danger-btn text-white btn-size"
                    :disabled="isDeletingId === item.id"
                    @click="deleteLine(item)"
                  >
                    {{ isDeletingId === item.id ? "Deleting..." : "Delete" }}
                  </b-button>
                </template>
              </b-table>
              </div>

              <a href="/budgets" class="btn akkurate-gray-btn text-white btn-size">Back to Budgets</a>
            </div>
          </div>
        </div>
      </div>
      <footer></footer>
    </div>
    <a class="d-inline scroll-to-top" href="#page-top"><i class="fas fa-angle-up"></i></a>
  </div>
</template>

<script>
import SidebarView from "@/views/main/components/Sidebar.vue";
import TopbarView from "@/views/main/components/Topbar.vue";
import ApiService from "@/core/services/api.service";
import Vue from "vue";
import { hasPermission } from "@/helper";

export default {
  name: "BudgetLines",
  components: { SidebarView, TopbarView },
  data() {
    return {
      isBusy: false,
      isUploading: false,
      isUpserting: false,
      isDeletingId: null,
      noAccess: false,
      budget: null,
      lines: [],
      branchOptions: [],
      coaSubheadOptions: [],
      filters: {
        month: "",
        branch_id: "",
        coa_subhead_id: "",
        per_page: 50,
      },
      upsert: {
        coa_subhead_id: "",
        branch_id: "",
        month: "",
        budget_amount: "",
      },
      uploadError: {
        message: "",
        errors: [],
      },
      uploadErrorFields: [
        { key: "row", label: "Row" },
        { key: "error", label: "Error" },
      ],
      lineFields: [
        { key: "code", label: "Code" },
        { key: "coa_name", label: "COA Name" },
        { key: "branch_id", label: "Branch" },
        { key: "month", label: "Month" },
        { key: "budget_amount", label: "Budget (GH¢)" },
        { key: "actions", label: "" },
      ],
    };
  },
  computed: {
    budgetId() {
      return this.$route?.params?.budgetId;
    },
    acceptedFileTypes() {
      return ".xlsx,.xls,.csv";
    },
    canManage() {
      return hasPermission("budgets_manage") && !this.noAccess;
    },
    isDraft() {
      return String(this.budget?.status || "").toLowerCase() === "draft";
    },
    canImportExcel() {
      return this.canManage && this.isDraft;
    },
    canUpsert() {
      return (
        this.canManage &&
        this.isDraft &&
        this.upsert.coa_subhead_id &&
        this.upsert.branch_id &&
        this.upsert.month &&
        this.upsert.budget_amount !== ""
      );
    },
  },
  created() {
    if (this.canManage) {
      this.loadBudgetContext();
    }
  },
  watch: {
    budgetId() {
      if (this.canManage) {
        this.loadBudgetContext();
      }
    },
  },
  methods: {
    async loadBudgetContext() {
      this.noAccess = false;
      this.uploadError = { message: "", errors: [] };
      await Promise.all([this.loadBudget(), this.loadDropdowns(), this.loadLines()]);
    },
    async loadBudget() {
      // best-effort: budget header to check status (draft gate)
      try {
        const res = await ApiService.get(`/budgets/${this.budgetId}`, { silent403: true });
        this.budget = res.data?.data ?? res.data ?? null;
      } catch (e) {
        this.budget = null;
      }
    },
    async loadLines() {
      this.isBusy = true;
      try {
        const res = await ApiService.get(`/budgets/${this.budgetId}/lines`, {
          params: {
            month: this.filters.month || undefined,
            branch_id: this.filters.branch_id || undefined,
            coa_subhead_id: this.filters.coa_subhead_id || undefined,
            per_page: this.filters.per_page || undefined,
          },
          silent403: true,
        });
        const list = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];
        this.lines = list.map((l) => ({
          ...l,
          budget_amount: this.displayNumber(Number(l.budget_amount) || 0),
        }));
      } catch (e) {
        this.lines = [];
      } finally {
        this.isBusy = false;
      }
    },
    async loadDropdowns() {
      try {
        const res = await ApiService.query("/reports/controls/products/dropdown");
        this.branchOptions = res.data?.branches || [];
      } catch (e) {
        this.branchOptions = [];
      }
      try {
        const res = await ApiService.post("/finance/chart-of-accounts/subhead/dropdown");
        this.coaSubheadOptions = res.data?.coa_subheads || [];
      } catch (e) {
        this.coaSubheadOptions = [];
      }
    },
    async upsertLine() {
      if (!this.canUpsert) return;
      this.isUpserting = true;
      try {
        await ApiService.post(`/budgets/${this.budgetId}/lines/upsert`, {
          coa_subhead_id: this.upsert.coa_subhead_id,
          branch_id: this.upsert.branch_id,
          month: this.upsert.month,
          budget_amount: this.upsert.budget_amount,
        }, { silent403: true });
        if (window.toast?.fire) {
          window.toast.fire({ icon: "success", title: "Budget line saved successfully" });
        }
        await this.loadLines();
      } catch (e) {
        if (e?.response?.status === 403 && window.toast?.fire) {
          window.toast.fire({ icon: "error", title: "No access" });
        }
      } finally {
        this.isUpserting = false;
      }
    },
    async deleteLine(item) {
      if (!this.canManage || !this.isDraft) return;
      this.isDeletingId = item?.id;
      try {
        await ApiService.delete(`/budgets/${this.budgetId}/lines/${item.id}`, { silent403: true });
        if (window.toast?.fire) {
          window.toast.fire({ icon: "success", title: "Budget line deleted successfully" });
        }
        await this.loadLines();
      } catch (e) {
        if (e?.response?.status === 403 && window.toast?.fire) {
          window.toast.fire({ icon: "error", title: "No access" });
        }
      } finally {
        this.isDeletingId = null;
      }
    },
    triggerFilePicker() {
      this.uploadError = { message: "", errors: [] };
      this.$refs.excelInput?.click?.();
    },
    async onFileSelected(evt) {
      const file = evt?.target?.files?.[0];
      // reset so selecting the same file again retriggers change
      evt.target.value = "";
      if (!file) return;
      if (!this.canImportExcel) return;

      this.isUploading = true;
      this.uploadError = { message: "", errors: [] };

      const form = new FormData();
      form.append("excelFileInput", file);

      try {
        await Vue.axios.post(`/budgets/${this.budgetId}/lines/import-excel`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          silent403: true,
        });
        if (window.toast?.fire) {
          window.toast.fire({
            icon: "success",
            title: "Budget lines imported successfully",
          });
        }
        await this.loadLines();
      } catch (e) {
        const status = e?.response?.status;
        if (status === 403) {
          if (window.toast?.fire) {
            window.toast.fire({ icon: "error", title: "No access" });
          }
          this.uploadError = {
            message: "No access: you do not have permission to import budget lines.",
            errors: [],
          };
          return;
        }
        if (status === 422) {
          const data = e?.response?.data || {};
          this.uploadError = {
            message: data.message || "Validation failed while importing budget lines.",
            errors: Array.isArray(data.errors) ? data.errors : [],
          };
          return;
        }
        this.uploadError = {
          message: e?.response?.data?.message || "Import failed. Please try again.",
          errors: [],
        };
      } finally {
        this.isUploading = false;
      }
    },
    downloadTemplate() {
      const header = ["code", "coa_name", "branch_id", "month", "budget_amount"];
      const example = ["13001", "Cash at hand", "1", `${new Date().getFullYear()}-01`, "1000.00"];
      const csv = [header.join(","), example.join(",")].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `budget_lines_template_${this.budgetId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

